import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface GlobalShortcutOptions {
  onOpenCommandPalette: () => void;
  onOpenShortcutsModal: () => void;
  onOpenTrainingGuide: () => void;
}

/**
 * Registers global keyboard shortcuts for the entire app.
 *
 * Shortcuts:
 *   Ctrl+K       → open command palette
 *   Ctrl+/       → open shortcuts reference modal
 *   ?            → open training guide for current module
 *   G then D     → /  (Dashboard)
 *   G then C     → /crm
 *   G then W     → /whatsapp
 *   G then I     → /inventory
 *   G then P     → /pricing
 *   G then M     → /marketplace
 *   G then A     → /attendance
 */
export function useGlobalShortcuts({
  onOpenCommandPalette,
  onOpenShortcutsModal,
  onOpenTrainingGuide,
}: GlobalShortcutOptions): void {
  const navigate = useNavigate();
  const gPressed = useRef(false);
  const gTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGMode = useCallback(() => {
    gPressed.current = false;
    if (gTimer.current) {
      clearTimeout(gTimer.current);
      gTimer.current = null;
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Ctrl+K — command palette (always, even in inputs)
      if (e.ctrlKey && e.key === "k") {
        e.preventDefault();
        onOpenCommandPalette();
        clearGMode();
        return;
      }

      // Ctrl+/ — shortcuts modal (always)
      if (e.ctrlKey && e.key === "/") {
        e.preventDefault();
        onOpenShortcutsModal();
        clearGMode();
        return;
      }

      // Skip text-editing shortcuts when focused in an input
      if (isInput) return;

      // ? — training guide
      if (e.key === "?" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        onOpenTrainingGuide();
        clearGMode();
        return;
      }

      // G-chord navigation
      if (e.key === "g" || e.key === "G") {
        if (!gPressed.current) {
          gPressed.current = true;
          // Auto-reset after 1.5s if no second key pressed
          gTimer.current = setTimeout(clearGMode, 1500);
        }
        return;
      }

      if (gPressed.current) {
        clearGMode();
        switch (e.key.toLowerCase()) {
          case "d": e.preventDefault(); navigate("/");            break;
          case "c": e.preventDefault(); navigate("/crm");         break;
          case "w": e.preventDefault(); navigate("/whatsapp");    break;
          case "i": e.preventDefault(); navigate("/inventory");   break;
          case "p": e.preventDefault(); navigate("/pricing");     break;
          case "m": e.preventDefault(); navigate("/marketplace"); break;
          case "a": e.preventDefault(); navigate("/attendance");  break;
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      clearGMode();
    };
  }, [navigate, onOpenCommandPalette, onOpenShortcutsModal, onOpenTrainingGuide, clearGMode]);
}
